import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { hasPermission, permissionForPath, needsPackageGate, PAGE_PERMISSIONS } from "@/lib/permissions";
import PackageGateOverlay from "@/components/PackageGateOverlay";
import NotificationBell from "@/components/NotificationBell";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  LayoutDashboard, GraduationCap, Users, School, Target,
  NotebookPen, LayoutGrid, MessageSquareReply, LogOut, Menu, Layers, Settings, Sparkles, CalendarDays, Shield, Package, FileText,
} from "lucide-react";
import Logo from "@/components/Logo";

const NAV_MAIN = [
  { to: "/adminxx1/dashboard", label: "Panel", icon: LayoutDashboard, testid: "sidebar-dashboard", permission: "dashboard" },
  { to: "/adminxx1/students", label: "Öğrenciler", icon: GraduationCap, testid: "sidebar-students", permission: "students" },
  { to: "/adminxx1/parents", label: "Veliler", icon: Users, testid: "sidebar-parents", permission: "parents" },
  { to: "/adminxx1/teachers", label: "Öğretmenler", icon: School, testid: "sidebar-teachers", permission: "teachers" },
  { to: "/adminxx1/bep", label: "BEP Modülü", icon: Target, testid: "sidebar-bep", permission: "bep" },
  { to: "/adminxx1/notes", label: "Günlük Notlar", icon: NotebookPen, testid: "sidebar-notes", permission: "notes" },
  { to: "/adminxx1/tasks", label: "Aile Ev Ödevi", icon: LayoutGrid, testid: "sidebar-tasks", permission: "tasks" },
  { to: "/adminxx1/activity-pool", label: "Etkinlik Havuzu", icon: CalendarDays, testid: "sidebar-activity-pool", permission: "activity_pool" },
  { to: "/adminxx1/feedback", label: "Geri Bildirimler", icon: MessageSquareReply, testid: "sidebar-feedback", permission: "feedback" },
  { to: "/adminxx1/pools", label: "Gelişimsel Profil", icon: Layers, testid: "sidebar-pools", permission: "pools" },
  { to: "/adminxx1/ai-reports", label: "AI Gelişim Raporu", icon: Sparkles, testid: "sidebar-ai-reports", permission: "ai_reports" },
  { to: "/adminxx1/settings", label: "Site Ayarları", icon: Settings, testid: "sidebar-settings", permission: "settings" },
  { to: "/adminxx1/pages", label: "Sayfalar", icon: FileText, testid: "sidebar-pages", permission: "pages" },
];

const NAV_ADMIN = [
  { to: "/adminxx1/roles", label: "Rol Yetkileri", icon: Shield, testid: "sidebar-roles", permission: "roles" },
  { to: "/adminxx1/packages", label: "Paketler", icon: Package, testid: "sidebar-packages", permission: "packages" },
];

function NavItem({ to, label, icon: Icon, testid, onNavigate }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? "bg-slate-800 text-white border-l-2 border-violet-500"
            : "text-slate-400 hover:bg-slate-800/50 hover:text-white"
        }`
      }
    >
      <Icon className="h-[18px] w-[18px]" />
      {label}
    </NavLink>
  );
}

function SidebarInner({ user, onLogout, onNavigate, goProfile }) {
  const mainNav = NAV_MAIN.filter((n) => hasPermission(user, n.permission));
  const adminNav = NAV_ADMIN.filter((n) => hasPermission(user, n.permission));

  return (
    <div className="flex h-full flex-col bg-slate-900">
      <div className="px-6 py-6">
        <Logo className="h-9 w-auto object-contain" alt="RENKA ÖZEL EĞİTİM" data-testid="sidebar-logo" />
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">Özel Eğitim</p>
        <p className="text-[11px] font-medium text-slate-300">Yönetici Paneli</p>
      </div>

      <nav className="sidebar-nav-scroll flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {mainNav.map((item) => (
          <NavItem key={item.to} {...item} onNavigate={onNavigate} />
        ))}

        {adminNav.length > 0 && (
          <>
            <div className="pb-1 pt-4">
              <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Erişim Yönetimi</p>
            </div>
            {adminNav.map((item) => (
              <NavItem key={item.to} {...item} onNavigate={onNavigate} />
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-slate-800 p-4">
        <button
          onClick={goProfile}
          data-testid="sidebar-profile-button"
          className="mb-3 flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-slate-800"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-600 text-sm font-semibold text-white">
            {(user?.name || "A").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-white">{user?.name || "Admin"}</p>
            <p className="truncate text-xs text-slate-400">{user?.email}</p>
          </div>
        </button>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="h-4 w-4" />
          Çıkış Yap
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const permKey = permissionForPath(location.pathname);
  const gated = needsPackageGate(user, permKey);
  const pageLabel = PAGE_PERMISSIONS.find((p) => p.key === permKey)?.label;

  useEffect(() => { document.title = "Yönetim Paneli - Renka"; }, []);

  useEffect(() => {
    if (!gated) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [gated]);

  const handleLogout = async () => { await logout(); navigate("/adminxx1"); };
  const goProfile = () => { setMobileOpen(false); navigate("/adminxx1/profile"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 overflow-hidden bg-slate-900 lg:flex" data-testid="sidebar">
        <SidebarInner user={user} onLogout={handleLogout} goProfile={goProfile} />
      </aside>

      <div className="sticky top-0 z-30 flex items-center justify-between bg-slate-900 px-4 py-3 lg:hidden">
        <Logo className="h-7 w-auto object-contain" alt="RENKA" />
        <div className="flex items-center gap-1">
          <NotificationBell variant="dark" />
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" aria-label="Menü" className="rounded-lg p-2 text-slate-300 hover:bg-slate-800">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-0 bg-slate-900 p-0" data-testid="mobile-sidebar">
              <SheetTitle className="sr-only">Menü</SheetTitle>
              <SidebarInner
                user={user}
                onLogout={handleLogout}
                goProfile={goProfile}
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <main className="lg:ml-64">
        <div className="sticky top-0 z-20 hidden items-center justify-end border-b border-slate-200/80 bg-white/90 px-6 py-2.5 backdrop-blur-xl lg:flex">
          <NotificationBell variant="light" />
        </div>
        <div className={gated ? "pointer-events-none select-none blur-[6px]" : ""}>
          {children}
        </div>
        {gated && <PackageGateOverlay pageLabel={pageLabel} />}
      </main>
    </div>
  );
}
